import { palette } from '@openx/components-core/lib/theme/paletteV2';
import type { DealInsightsBidRateWinRateApi, DealInsightsSpendApi } from '@openx/types/dealInsights';
import { format, isValid, parseISO } from 'date-fns';
import type { LabelPosition } from 'recharts/types/component/Label';

export const MOCK_DATA: DealInsightsSpendApi[] = [
  { date: '2024-01-16T00:00:00.000Z', spend: 1800000, ecpm: 3.2, viewability: 0.75, vcr: 0.65, impressions: 1000 },
  { date: '2024-01-17T00:00:00.000Z', spend: 1500000, ecpm: 2.8, viewability: 0.75, vcr: 0.65, impressions: 1000 },
  { date: '2024-01-18T00:00:00.000Z', spend: 2200000, ecpm: 4.1, viewability: 0.75, vcr: 0.65, impressions: 1000 },
  { date: '2024-01-19T00:00:00.000Z', spend: 1900000, ecpm: 3.5, viewability: 0.75, vcr: 0.65, impressions: 1000 },
  { date: '2024-01-20T00:00:00.000Z', spend: 1600000, ecpm: 2.9, viewability: 0.75, vcr: 0.65, impressions: 1000 },
  { date: '2024-01-21T00:00:00.000Z', spend: 2100000, ecpm: 3.8, viewability: 0.75, vcr: 0.65, impressions: 1000 },
];

export const MOCK_BID_RATE_WIN_RATE_DATA: DealInsightsBidRateWinRateApi[] = [
  {
    date: '2024-01-16T00:00:00.000Z',
    bid_rate: 0.0004,
    win_rate: 0.06,
    impressions: 1000,
    viewability: 0.85,
    vcr: 0.8,
  },
  {
    date: '2024-01-17T00:00:00.000Z',
    bid_rate: 0.0005,
    win_rate: 0.07,
    impressions: 1000,
    viewability: 0.85,
    vcr: 0.8,
  },
  {
    date: '2024-01-18T00:00:00.000Z',
    bid_rate: 0.0006,
    win_rate: 0.08,
    impressions: 1000,
    viewability: 0.85,
    vcr: 0.8,
  },
  {
    date: '2024-01-19T00:00:00.000Z',
    bid_rate: 0.00045,
    win_rate: 0.075,
    impressions: 1000,
    viewability: 0.85,
    vcr: 0.8,
  },
  {
    date: '2024-01-20T00:00:00.000Z',
    bid_rate: 0.00035,
    win_rate: 0.065,
    impressions: 1000,
    viewability: 0.85,
    vcr: 0.8,
  },
  {
    date: '2024-01-21T00:00:00.000Z',
    bid_rate: 0.0007,
    win_rate: 0.085,
    impressions: 1000,
    viewability: 0.85,
    vcr: 0.8,
  },
];

export const calculateTickStep = (range: number, target = 5) => {
  const base = range > 0 ? range / target : 1;
  const exp = Math.floor(Math.log10(base));
  const frac = base / 10 ** exp;
  const niceFrac = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;

  return niceFrac * 10 ** exp;
};

export const buildTicks = (maxValue: number) => {
  if (maxValue === 0) {
    return { ticks: [0, 0.2, 0.4, 0.6, 0.8], domain: [0, 0.8] as [number, number], top: 0.8 };
  }
  const targetTop = maxValue * 1.1;

  const step = calculateTickStep(targetTop, 4);
  const niceTop = Math.ceil(targetTop / step) * step;

  const tickStep = niceTop / 4;
  const ticks: number[] = [];

  for (let i = 0; i < 5; i++) {
    ticks.push(i * tickStep);
  }

  return { ticks, domain: [0, niceTop] as [number, number], top: niceTop };
};

export const buildLabel = (
  label: string
): { value: string; angle: number; position: LabelPosition; offset: number } => ({
  value: label,
  angle: 0,
  position: 'insideTop',
  offset: -6,
});

const toDateString = (value: string | number | Date | null): string => {
  if (value === null || value === undefined) return 'null';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'number') return new Date(value).toISOString();
  return value;
};

type TickProps = {
  x: string | number;
  y: string | number;
  payload: { value: string | number | Date };
  isLoading: boolean;
};

export type YAxisTickProps = TickProps & {
  payload: { value: number };
  leftScale: ReturnType<typeof buildTicks>;
  rightScale: ReturnType<typeof buildTicks>;
  isLeftAxis: boolean;
  formatter: (value: number) => string;
};

export const XAxisTick = ({ x, y, payload, isLoading }: TickProps) => {
  if (isLoading) {
    return (
      <g transform={`translate(${x},${y})`}>
        <rect
          x={-12}
          y={16}
          width={72}
          height={16}
          fill={palette.divider}
          opacity={0.5}
          rx={4}
          transform="rotate(-30, 24, 64)"
        />
      </g>
    );
  }

  const dateValue = toDateString(payload.value);
  const d = parseISO(dateValue);
  const formattedDate = isValid(d) ? format(d, 'MMM d, yyyy') : String(payload.value);

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} textAnchor="middle" fill={palette.text.secondary} transform="rotate(-30, 36, 0)">
        {formattedDate}
      </text>
    </g>
  );
};

export const YAxisTick = ({
  x,
  y,
  payload,
  isLoading,
  leftScale,
  rightScale,
  isLeftAxis,
  formatter,
}: YAxisTickProps) => {
  if (isLoading) {
    const scale = isLeftAxis ? leftScale : rightScale;
    const shouldHideValue = payload.value === scale.top;

    if (shouldHideValue) {
      return null;
    }

    const offsetX = isLeftAxis ? -32 : 0;
    return (
      <g transform={`translate(${x},${y})`}>
        <rect x={offsetX} y={-6} width={32} height={12} fill={palette.divider} opacity={0.5} rx={4} />
      </g>
    );
  }

  const scale = isLeftAxis ? leftScale : rightScale;
  const shouldHideValue = payload.value === scale.top;
  const formattedValue = shouldHideValue ? '' : formatter(payload.value);

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={3} textAnchor={isLeftAxis ? 'end' : 'start'} fill={palette.text.primary}>
        {formattedValue}
      </text>
    </g>
  );
};
